import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { FileText, Users, Car, Settings, LogOut, Home, Calendar, BarChart3, Search, Bell, Menu, X, Plus, Clock, BookOpen, CheckSquare, FolderOpen, Calculator, Shield } from 'lucide-react';
import { Input } from './ui/input';

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAkteDropdown, setShowAkteDropdown] = useState(false);

  // Bei Desktop standardmäßig Sidebar öffnen
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('autogutachter24_user');
    const token = localStorage.getItem('autogutachter24_token');
    
    if (!storedUser || !token) {
      navigate('/');
      return;
    }
    
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('autogutachter24_user');
    localStorage.removeItem('autogutachter24_token');
    localStorage.removeItem('autogutachter24_stay_logged');
    navigate('/');
  };

  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/akten', icon: FileText, label: 'Akten' },
    { path: '/dokumente', icon: FolderOpen, label: 'Dokumente' },
    { path: '/buchhaltung', icon: BarChart3, label: 'Buchhaltung' },
    { path: '/kunden', icon: Users, label: 'Kunden' },
    { path: '/aufgaben', icon: CheckSquare, label: 'Aufgaben' },
    { path: '/termine', icon: Calendar, label: 'Termine' },
    { path: '/schulung', icon: BookOpen, label: 'Schulung' }
  ];

  // Admin-Menüpunkte (nur für Admin-Rolle oder Backoffice)
  const adminMenuItems = (user?.rolle === 'admin' || user?.rolle === 'backoffice') ? [
    { path: '/admin', icon: Shield, label: 'Admin-Bereich', highlight: true }
  ] : [];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 bottom-0 bg-white border-r border-gray-200 z-40
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full lg:w-20 lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex px-4 py-3 border-b border-gray-200 h-[73px] items-center">
            <div className="flex items-center justify-between w-full">
              <div className="flex-1 flex justify-center">
                <img 
                  src="/logo-dashboard.png" 
                  alt="Autogutachter24" 
                  className={`transition-all duration-300 ${sidebarOpen ? 'h-10 w-40' : 'h-8 w-8 lg:h-10 lg:w-10 object-contain'}`}
                />
              </div>
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden lg:block p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                {sidebarOpen ? <X className="w-4 h-4 text-gray-600" /> : <Menu className="w-4 h-4 text-gray-600" />}
              </button>
              {/* Mobile Close Button */}
              <button 
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title={!sidebarOpen ? item.label : ''}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className={`text-sm font-medium ${sidebarOpen ? 'block' : 'hidden lg:hidden'}`}>{item.label}</span>
                </button>
              );
            })}
            
            {/* Admin-Bereich */}
            {adminMenuItems.length > 0 && (
              <>
                <div className="border-t border-gray-200 my-3"></div>
                <div className={`px-4 py-1 ${sidebarOpen ? 'block' : 'hidden'}`}>
                  <span className="text-xs font-semibold text-gray-400 uppercase">Admin</span>
                </div>
                {adminMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        if (window.innerWidth < 1024) setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                          : item.highlight
                            ? 'text-orange-700 bg-orange-50 hover:bg-orange-100'
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      title={!sidebarOpen ? item.label : ''}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className={`text-sm font-medium ${sidebarOpen ? 'block' : 'hidden lg:hidden'}`}>{item.label}</span>
                    </button>
                  );
                })}
              </>
            )}
          </nav>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Top Header Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="px-3 sm:px-4 lg:px-6 py-3">
            <div className="flex items-center justify-between">
              {/* Left Section: Mobile Menu + Title + Neue Akte */}
              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* Mobile Menu Button */}
                <button 
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Menu className="w-5 h-5 text-gray-600" />
                </button>

                {/* Page Title - nur auf größeren Bildschirmen */}
                <h2 className="hidden sm:block text-lg lg:text-xl font-semibold text-gray-900">
                  {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
                </h2>

                {/* Neue Akte Dropdown Button */}
                <div className="relative">
                  <Button 
                    onClick={() => setShowAkteDropdown(!showAkteDropdown)}
                    className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <Plus className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Neue Akte</span>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                  
                  {/* Dropdown Menu */}
                  {showAkteDropdown && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowAkteDropdown(false)}
                      />
                      <div className="absolute left-0 mt-2 w-56 sm:w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                        <div className="p-2">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                            Was möchten Sie erstellen?
                          </div>
                          {['Gutachten', 'Kurzgutachten', 'Wertgutachten', 'Kostenvoranschlag', 'Kasko'].map((typ) => (
                            <button
                              key={typ}
                              onClick={() => {
                                navigate(`/neue-akte?typ=${typ}`);
                                setShowAkteDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-2"
                            >
                              <FileText className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-700">{typ}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Right Section */}
              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* Search - nur auf Desktop */}
                <div className="hidden md:flex items-center space-x-2 bg-gray-100 rounded-lg px-4 py-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Suchen..."
                    className="bg-transparent border-none outline-none text-sm w-32 lg:w-40"
                  />
                </div>

                {/* Mobile Search Button */}
                <button className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Search className="w-5 h-5 text-gray-600" />
                </button>

                {/* Notifications */}
                <button className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Bell className="w-5 h-5 text-gray-600" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>

                {/* User Profile Dropdown */}
                <div className="relative group">
                  <button className="flex items-center space-x-2 sm:space-x-3 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <div className="relative flex-shrink-0">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-medium text-xs sm:text-sm shadow">
                        {user.vorname?.[0]}{user.nachname?.[0]}
                      </div>
                      <span className="absolute bottom-0 right-0 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-green-400 border-2 border-white rounded-full"></span>
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium text-gray-900">{user.vorname} {user.nachname}</div>
                      <div className="text-xs text-gray-500">{user.rolle}</div>
                    </div>
                  </button>

                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="p-3 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">{user.vorname} {user.nachname}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                      <div className="text-xs text-blue-600 mt-1">Admin Demo</div>
                    </div>
                    <div className="p-2">
                      {[
                        { path: '/unternehmen', icon: BarChart3, label: 'Unternehmen' },
                        { path: '/einstellungen', icon: Settings, label: 'Einstellungen' },
                        { path: '/support', icon: FileText, label: 'Support' },
                        { path: '/chat', icon: Bell, label: 'Chat' },
                        { path: '/verbesserung', icon: Plus, label: 'Verbesserung' },
                      ].map((item) => (
                        <button 
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <item.icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{item.label}</span>
                        </button>
                      ))}
                      <div className="border-t border-gray-100 my-2"></div>
                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm font-medium">Abmelden</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-3 sm:p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
